import React, {useContext, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {IS_PHOTO_ASKED, setPhotoAsked} from 'entities/app'
import {UploadAvatar, setAvatar, setUser, useUser} from 'entities/user'
import {GetDirectUploadUrlPhoto, UploadMediaToBucket} from 'shared/api/images'
import {openMail} from 'shared/lib/links'
import {Button} from 'shared/ui/buttons'
import {Loader} from 'shared/ui/loader'
import {AuthModal} from 'shared/ui/modals'
import sharedStyles from 'shared/ui/styles/authModals.module.scss'
import {CropperContext, CropperContextType} from '../cropper'
import {Camera, Mail} from './assets/icons'
import customStyles from './styles.module.scss'

const MAX_IMAGE_SIZE = 12 * 1024 * 1024
const MIN_IMAGE_SIZES = 420
const pageUrl = window.location.pathname.split('/')[1]
const validUrls = ['terms-of-use', 'privacy-policy', 'community-rules', 'dating-safety']

export const PostRegisterModal = () => {
  const {profile} = useUser()
  const isPhotoAsked = localStorage.getItem(IS_PHOTO_ASKED)

  if (!(isPhotoAsked && JSON.parse(isPhotoAsked).value) && !profile.avatarUrl && !validUrls.includes(pageUrl)) return <UploadPhotoModal />

  return <></>
}

const UploadPhotoModal = () => {
  const user = useUser()
  const dispatch = useDispatch()
  const [error, setError] = useState('')
  const {setCropperConfig, closeCropper} = useContext(CropperContext) as CropperContextType
  const ref = useRef<HTMLInputElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const isPhotoAsked = localStorage.getItem(IS_PHOTO_ASKED)

  const skipHandler = () => {
    dispatch(setUser({...user, isPhotoAsked: true}))
    dispatch(setPhotoAsked(true))
  }

  if (user.isPhotoAsked || (isPhotoAsked && JSON.parse(isPhotoAsked).value)) skipHandler()

  const onClick = () => {
    setError('')
    ref?.current?.click()
  }

  const onSave = async (file: File) => {
    try {
      setIsLoading(true)
      const {data: uploadUrls} = await GetDirectUploadUrlPhoto({count: 1, mediaType: 'avatar'})
      const [{uploadUrl, key}] = uploadUrls

      await UploadMediaToBucket(uploadUrl, file)

      const {data: avatar} = await UploadAvatar(key)

      dispatch(setAvatar(avatar.image.url))
    } catch (error: any) {
      console.error(error)
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!(event.target.files && event.target.files.length > 0)) return
    if (event.target.files[0].size > MAX_IMAGE_SIZE) return setError('Max image size is 12 MB')

    const file = event.target.files[0]

    const imageDataUrl = await readFile(file)
    if (!imageDataUrl) return
    const img = new Image()

    img.src = URL.createObjectURL(file)
    img.onload = () => {
      if (img.width < MIN_IMAGE_SIZES || img.height < MIN_IMAGE_SIZES) return setError(`Min image width and height is ${MIN_IMAGE_SIZES} pixels`)
      onSave(file).then(() => event.target.value = '')
    }

  }

  return <AuthModal className={sharedStyles.modal}>
    <div className={sharedStyles.modal__titleContainer}>
      <div className={`${sharedStyles.modal__titleIcon} ${customStyles.cameraIcon}`}>
        <Camera />
      </div>
      <div className={sharedStyles.modal__textContainer}>
        <p className={sharedStyles.modal__title}>Add photo</p>
        <span className={sharedStyles.modal__title__description}>
          Users with photos get 40% more perfect matches
        </span>
      </div>
    </div>
    <div className={sharedStyles.modal__body}>
      <Button
        onClick={onClick}
        className={sharedStyles.modal__signIn}
        fullwidth
        styleType='primary'
        isDisabled={isLoading}
      >
        {isLoading ? <Loader className={customStyles.loader} /> : null}
        Upload photo
        <input
          type='file'
          accept='.jpeg, .png, .jpg, .webp'
          hidden
          ref={ref}
          onChange={onFileChange}
        />
      </Button>
      <span className={customStyles.photoInformation}>
          Please add photos in JPEG, JPG, PNG, WEBP formats only. Your photos must be at least {MIN_IMAGE_SIZES} x {MIN_IMAGE_SIZES} px and not bigger than 12MB.
      </span>
      {error ? <span className={customStyles.errorText}>{error}</span> : null}
      <Button onClick={skipHandler} as='unstyled' className={customStyles.skipBtn}>
        Skip
      </Button>
    </div>
  </AuthModal>
}

const ConfirmEmailModal: React.FC = () => {
  const user = useUser()
  const dispatch = useDispatch()

  if (user.profile.isConfirmed) return <></>

  const onClickHandler = () => {
    dispatch(setUser({...user, profile: {...user.profile, isConfirmed: true}}))
    openMail()
  }

  return (
    <AuthModal className={sharedStyles.modal}>
      <div className={sharedStyles.modal__titleContainer}>
        <div className={`${sharedStyles.modal__titleIcon} ${customStyles.mailIcon}`}><Mail /></div>
        <div className={sharedStyles.modal__textContainer}>
          <p className={sharedStyles.modal__title}>Check your email</p>
          <span className={sharedStyles.modal__title__description}>
            It's almost done!
          </span>
        </div>
        <div className={customStyles.border} />
      </div>
      <span className={customStyles.confirmText}>
        Please complete your account activation. Find our email in your inbox at
        <span className={`${customStyles.confirmText} ${customStyles.email}`}>
          {` ${user.profile.email} `}
        </span>
        and press the 'Confirm email' button in it.
      </span>
      <div className={sharedStyles.modal__buttons}>
        <Button
          styleType='primary'
          fullwidth
          className={sharedStyles.modal__signIn}
          onClick={onClickHandler}
        >
          Open email
        </Button>
        <Button
          styleType='secondary'
          fullwidth
          className={`${sharedStyles.modal__signIn} ${customStyles.resendBtn}`}
          onClick={onClickHandler}
        >
          Resend
        </Button>
      </div>
      <span className={sharedStyles.modal__caption} style={{alignSelf: 'center'}}>
        Delivery of the activation letter takes 5-10 minutes.
      </span>
    </AuthModal>
  )
}

const readFile = (file: File): Promise<string | ArrayBuffer | null> => new Promise((resolve) => {
  const reader = new FileReader()
  reader.addEventListener('load', () => resolve(reader.result), false)
  reader.readAsDataURL(file)
})